import { useQueryClient } from 'react-query';
import { MenuItem, QUERY_KEYS } from '@24i/nxg-sdk-photon';
import { useState } from 'react';
import { log } from '@24i/nxg-core-utils/src/logger';
import { useAppConfigQuery } from '../useAppConfigQuery';

import { FetchMenuParams, FetchMenuResult } from '../types';
import { handleError } from '../../../utils/error';
import { useAppStructureData } from '../../../context/AppStructureData';

export const useMenuFetch = (): FetchMenuResult<MenuItem[]> => {
    const queryClient = useQueryClient();
    const appStructureDataClient = useAppStructureData();
    const { appSettings } = useAppConfigQuery();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | undefined>(undefined);

    const fetch = async (params?: FetchMenuParams): Promise<MenuItem[]> => {
        try {
            setIsLoading(true);
            setError(undefined);

            if (params?.forceRefresh) {
                await queryClient.invalidateQueries(QUERY_KEYS.menu);
            }

            const menuId = params?.menuId || appSettings?.features?.menus?.main || undefined;
            const queryKey = menuId ? [QUERY_KEYS.menu, menuId] : QUERY_KEYS.menu;
            return await queryClient.fetchQuery({
                queryKey,
                queryFn: () => appStructureDataClient.fetchMenu(menuId, appSettings),
            });
        } catch (e) {
            const err = handleError(e, 'Failed to fetch menu');
            setError(err);
            log('Error fetching menu: ', err);
            return [];
        } finally {
            setIsLoading(false);
        }
    };

    return { fetch, isLoading, error };
};
